'use client';

import { c as _c } from "react/compiler-runtime";
import { CheckOutlined as CheckIcon, CopyOutlined as CopyIcon } from '@ant-design/icons';
import { type ComponentProps, createContext, type HTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import { type BundledLanguage, codeToHtml, type ShikiTransformer } from 'shiki';
import { Button } from '@/ui/molecules/button';
import { nmodl } from '@/ui/molecules/code-blocks/nmodl';
import { cn } from '@/utils/css-class';
import type { LanguageRegistration } from 'shiki';
type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: BundledLanguage;
  showLineNumbers?: boolean;
  scrollableX?: boolean;
  contentClassName?: string;
};
type CodeBlockContextType = {
  code: string;
  language: BundledLanguage;
};

/** Shiki always accepts this one, so it is a safe last resort. */
const PLAIN_TEXT = 'text' as BundledLanguage;

/** Languages shiki does not ship, pointed at our own grammar or the closest one it has. */
const EXTRA_LANGUAGES: Record<string, LanguageRegistration | BundledLanguage> = {
  mod: nmodl,
  nmodl,
  hoc: 'c'
};
const CodeBlockContext = createContext<CodeBlockContextType>({
  code: '',
  language: PLAIN_TEXT
});
type THastLikeElement = {
  children: Array<{
    type: string;
    tagName?: string;
    properties?: Record<string, unknown>;
    children?: Array<{
      type: string;
      value: string;
    }>;
  }>;
};
const lineNumberTransformer: ShikiTransformer = {
  name: 'line-numbers',
  line(node, line: number) {
    const hastNode = node as unknown as THastLikeElement;
    hastNode.children.unshift({
      type: 'element',
      tagName: 'span',
      properties: {
        className: ['inline-block', 'min-w-10', 'mr-4', 'text-right', 'select-none', 'text-muted-foreground']
      },
      children: [{
        type: 'text',
        value: String(line)
      }]
    });
  }
};
export async function highlightCode(code: string, language: BundledLanguage, showLineNumbers = false) {
  const transformers: ShikiTransformer[] = showLineNumbers ? [lineNumberTransformer] : [];
  const render = (lang: BundledLanguage | LanguageRegistration) =>
  // Use dual themes with CSS variables for class-based switching
  codeToHtml(code, {
    lang: lang as BundledLanguage,
    themes: {
      light: 'catppuccin-latte',
      dark: 'catppuccin-mocha'
    },
    defaultColor: false,
    // Use CSS variables instead of inline colors
    transformers
  });

  // shiki throws on an unknown language, and the block would be left blank
  // the routing side lowercases extensions, so match on the lowercased key
  return render(EXTRA_LANGUAGES[language.toLowerCase()] ?? language).catch(() => render(PLAIN_TEXT));
}
export function CodeBlock(t0) {
  const $ = _c(36);
  let children;
  let className;
  let code;
  let contentClassName;
  let language;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      code,
      language,
      showLineNumbers: t1,
      scrollableX: t2,
      contentClassName,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = code;
    $[4] = contentClassName;
    $[5] = language;
    $[6] = props;
    $[7] = t1;
    $[8] = t2;
  } else {
    children = $[1];
    className = $[2];
    code = $[3];
    contentClassName = $[4];
    language = $[5];
    props = $[6];
    t1 = $[7];
    t2 = $[8];
  }
  const showLineNumbers = t1 === undefined ? false : t1;
  const scrollableX = t2 === undefined ? false : t2;
  const [html, setHtml] = useState("");
  const mounted = useRef(false);
  let t3;
  let t4;
  if ($[9] !== code || $[10] !== language || $[11] !== showLineNumbers) {
    t3 = () => {
      highlightCode(code, language, showLineNumbers).then(highlighted => {
        if (!mounted.current) {
          setHtml(highlighted);
          mounted.current = true;
        }
      });
      return () => {
        mounted.current = false;
      };
    };
    t4 = [code, language, showLineNumbers];
    $[9] = code;
    $[10] = language;
    $[11] = showLineNumbers;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  if ($[14] !== code || $[15] !== language) {
    t5 = {
      code,
      language
    };
    $[14] = code;
    $[15] = language;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const t6 = scrollableX ? "overflow-x-auto overflow-y-hidden" : "overflow-hidden";
  let t7;
  if ($[17] !== className || $[18] !== t6) {
    t7 = cn("group bg-background text-foreground border-neutral-light relative w-full rounded-md border", t6, className);
    $[17] = className;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const t8 = scrollableX ? "secondary-scrollbar overflow-x-auto overflow-y-hidden [&>pre]:min-w-max [&>pre]:w-max" : "overflow-hidden";
  let t9;
  if ($[20] !== contentClassName || $[21] !== t8) {
    t9 = cn("[&>pre]:bg-background! [&>pre]:text-foreground! [&_code]:font-mono [&_code]:text-sm [&>pre]:m-0 [&>pre]:p-4 [&>pre]:text-sm", t8, contentClassName);
    $[20] = contentClassName;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== html) {
    t10 = {
      __html: html
    };
    $[23] = html;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t9) {
    t11 = <div className="relative"><div className={t9} dangerouslySetInnerHTML={t10} /></div>;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== children || $[29] !== props || $[30] !== t11 || $[31] !== t7) {
    t12 = <div className={t7} {...props}>{children}{t11}</div>;
    $[28] = children;
    $[29] = props;
    $[30] = t11;
    $[31] = t7;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== t12 || $[34] !== t5) {
    t13 = <CodeBlockContext.Provider value={t5}>{t12}</CodeBlockContext.Provider>;
    $[33] = t12;
    $[34] = t5;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
}
export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
  iconClassName?: string;
};
export function CodeBlockLanguageLabel(t0) {
  const $ = _c(6);
  const {
    className,
    title
  } = t0;
  const {
    language
  } = useContext(CodeBlockContext);
  let t1;
  if ($[0] !== className) {
    t1 = cn("rounded-full border border-gray-300 px-4 py-1 text-xs", "font-medium tracking-wide text-gray-500 uppercase", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== language || $[3] !== t1 || $[4] !== title) {
    t2 = <span title={title} className={t1}>{language}</span>;
    $[2] = language;
    $[3] = t1;
    $[4] = title;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
export function CodeBlockCopyButton({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  iconClassName,
  ...props
}: CodeBlockCopyButtonProps) {
  const [isCopied, setIsCopied] = useState(false);
  const {
    code
  } = useContext(CodeBlockContext);
  const copyToClipboard = async () => {
    if (typeof window === 'undefined' || !navigator?.clipboard?.writeText) {
      onError?.(new Error('Clipboard API not available'));
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      onCopy?.();
      setTimeout(() => setIsCopied(false), timeout);
    } catch (error) {
      onError?.(error as Error);
    }
  };
  const Icon = isCopied ? CheckIcon : CopyIcon;
  return <Button className={cn('shrink-0', className)} onClick={copyToClipboard} variant="icon" {...props}>
      {children ?? <Icon size={14} className={iconClassName} />}
    </Button>;
}